import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { trackError, toTrackedError } from '@src/core/sentry';
import { NOTIFICATION_TYPE_DEFAULTS, NOTIFICATION_TYPE_ORDER, NotificationSettingEntity, NotificationType } from '../entities';
import {
  useGetNotificationSettingsQuery,
  useResetNotificationSettingsMutation,
  useUpdateNotificationSettingsMutation,
} from '../redux';

// Merge the server response onto the full type list so every toggle renders even
// if the backend omits a type (falls back to its documented default).
const mergeSettings = (settings: NotificationSettingEntity[] = []): NotificationSettingEntity[] =>
  NOTIFICATION_TYPE_ORDER.map((type) => {
    const found = settings.find((s) => s.type === type);
    return { type, enabled: found ? found.enabled : NOTIFICATION_TYPE_DEFAULTS[type] };
  });

// Shared notification-settings controller: local toggle state synced from the
// server, optimistic save-on-toggle, and reset-to-defaults. Consumed by both the
// profile and the settings notification pages.
export const useNotificationSettings = () => {
  const { t } = useTranslation();

  const { data, isLoading } = useGetNotificationSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] = useUpdateNotificationSettingsMutation();
  const [resetSettings, { isLoading: isResetting }] = useResetNotificationSettingsMutation();

  const [settings, setSettings] = React.useState<NotificationSettingEntity[]>(mergeSettings());

  React.useEffect(() => {
    setSettings(mergeSettings(data));
  }, [data]);

  const persist = React.useCallback(
    (next: NotificationSettingEntity[], previous: NotificationSettingEntity[]) => {
      updateSettings(next)
        .unwrap()
        .then(() => toast.success(t('notifications.settingsPage.saved')))
        .catch((error: unknown) => {
          setSettings(previous);
          trackError(toTrackedError(error), { code: 'FE_0018', extra: { flow: 'updateNotificationSettings' } });
          toast.error(t('notifications.settingsPage.saveFailed'));
        });
    },
    [updateSettings, t],
  );

  const onToggle = React.useCallback(
    (type: NotificationType) => {
      setSettings((prev) => {
        const next = prev.map((s) => (s.type === type ? { ...s, enabled: !s.enabled } : s));
        persist(next, prev);
        return next;
      });
    },
    [persist],
  );

  const onReset = React.useCallback(() => {
    resetSettings()
      .unwrap()
      .then((restored) => {
        setSettings(mergeSettings(restored));
        toast.success(t('notifications.settingsPage.resetSuccess'));
      })
      .catch((error: unknown) => {
        trackError(toTrackedError(error), { code: 'FE_0018', extra: { flow: 'resetNotificationSettings' } });
        toast.error(t('notifications.settingsPage.resetFailed'));
      });
  }, [resetSettings, t]);

  return { settings, isLoading, isSaving, isResetting, onToggle, onReset };
};
